/**
 * 등락·손익 색 3갈래(상승·하락·보합) — 공용. TRAD-3665(포트폴리오)에서 만들어 TRAD-3669 에서 shared 로 옮겼다
 * (투표·마이페이지 포인트·부동산 홈·포인트 페이지가 같은 판정을 쓴다).
 *
 * TRAD-3665 — 손익·등락 색 3갈래(상승·하락·보합). 종전 `>= 0 ? 상승 : 하락` 이분법은 0 을 상승색(빨강)으로 칠했다.
 * 판정은 **화면에 보이는 반올림 결과**로 한다(율 = 소수 2자리 · 금액 = 원 정수) — «0.00%» 인데 색이 붙지 않게
 * (저널 TRAD-1946 C3④ 와 같은 규칙). 보합 = 정본 토큰 `text-price-even`(저널 getReturnColorClass 와 같은 토큰).
 * 🔸 부호(«+0원»·«+0.00%»)는 이번 범위가 아니다 — PortfolioPage-total-pnl-sign.test 가 «0 → +0원» 을 현행 정책으로 고정.
 */
export type PriceTone = 'rise' | 'fall' | 'even';
export const priceTone = (value: number, fractionDigits: number): PriceTone => {
  const shown = Number(value.toFixed(fractionDigits));
  return shown > 0 ? 'rise' : shown < 0 ? 'fall' : 'even';
};

export const PRICE_TONE_TEXT: Record<PriceTone, string> = {
  rise: 'text-price-rise',
  fall: 'text-price-fall',
  even: 'text-price-even',
};

/**
 * TRAD-3670(davidyoon 결정 2026-10-09) — **0 은 부호 없이 «0»**. 0 판정은 priceTone 과 같은 «화면에 보이는 반올림 결과».
 * 종전 표기 2갈래가 둘 다 틀렸다: `>= 0 ? '+'` 는 0 에 «+0.00%», `> 0 ? '+'` + toFixed 는 반올림 0(0.004)에 «+0.00%»,
 * −0.004 에 «-0.00%»(toFixed 가 음수 0 에 «-» 를 남긴다).
 * 반환: 상승 '+' · 하락 '-' · 보합 ''(빈 문자열). 숫자 자체는 호출부가 **절댓값**으로 포맷해 붙인다(Intl·toFixed 의 «-0» 회피).
 */
export const signOf = (value: number, fractionDigits: number): '+' | '-' | '' => {
  const tone = priceTone(value, fractionDigits);
  return tone === 'rise' ? '+' : tone === 'fall' ? '-' : '';
};

/**
 * `toFixed` 표기 + 부호(TRAD-3670). 1.234 → «+1.23» · −1.234 → «-1.23» · 0 · 0.004 · −0.004 → «0.00».
 * 0 이 아닌 값은 종전 `${v > 0 ? '+' : ''}${v.toFixed(d)}` 와 **한 글자도** 같다(toFixed 는 음수를 −(|v|.toFixed) 로 만든다).
 * 단위(%·p·bp)는 호출부가 붙인다.
 */
export const signedFixed = (value: number, fractionDigits: number): string =>
  `${signOf(value, fractionDigits)}${Math.abs(value).toFixed(fractionDigits)}`;
